import { useState, useMemo } from 'react'
import { Check } from 'lucide-react'
import { HoverableImage } from '@/components/ui/hoverable-image'
import SearchAndFilter from '../SearchAndFilter'

const SelectionIcon = () => (
    <div className="tw:bg-blue-300 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:w-6 tw:h-6">
        <Check size={14} className="tw:text-white" />
    </div>
)

const Photo = ({ photo, isSelected, onPhotoSelect, source }) => {
    // Calculate width based on aspect ratio for 130px height
    const calculatedWidth = photo.width && photo.height ? Math.round(130 * (photo.width / photo.height)) : 130 // fallback to square if dimensions unknown

    return (
        <div
            key={photo.id}
            className="tw:relative tw:cursor-pointer tw:space-y-1"
            onClick={() => onPhotoSelect(photo.id, source, photo.url)}
        >
            <div className="tw:relative tw:flex tw:flex-col tw:gap-2">
                <HoverableImage
                    src={photo.url}
                    alt={photo.name || 'Media library image'}
                    trigger="hover"
                    tooltipSize="medium"
                    className="tw:rounded-xs tw:object-cover"
                    style={{ width: `${calculatedWidth}px`, height: '130px' }}
                    thumbnailClassName="tw:transition-transform tw:duration-200 tw:hover:tw:scale-[1.02]"
                    expandedClassName="tw:rounded-xs tw:shadow-xl"
                    footer={
                        <div className="tw:text-sm">
                            <div className="tw:font-medium tw:truncate">{photo.name}</div>
                            <div className="tw:text-gray-200">
                                {photo.width}×{photo.height}
                            </div>
                        </div>
                    }
                />

                {/* Selection Checkmark */}
                {isSelected && (
                    <div className="tw:absolute tw:top-1 tw:left-1 tw:rounded-full">
                        <SelectionIcon />
                    </div>
                )}
            </div>

            {/* Image metadata */}
            <div className="tw:flex tw:flex-col" style={{ width: `${calculatedWidth}px` }}>
                <div className="tw:text-sm tw:truncate">{photo.name}</div>
                <div className="tw:text-gray-200 tw:text-sm">
                    {photo.width}x{photo.height}
                </div>
            </div>
        </div>
    )
}

const MediaLibraryGrid = ({
    photos = [],
    selectedPhotos = [],
    onPhotoSelect,
    searchQuery = '',
    onSearchChange,
    loading = false,
    photoSources = {},
}) => {
    const [internalSearch, setInternalSearch] = useState('')

    // Use external search if provided, otherwise internal
    const effectiveSearch = searchQuery !== undefined ? searchQuery : internalSearch
    const effectiveOnSearchChange = onSearchChange || setInternalSearch

    // Filter photos based on search
    const filteredPhotos = useMemo(() => {
        if (!effectiveSearch) return photos

        const searchLower = effectiveSearch.toLowerCase()
        return photos.filter((photo) => {
            const name = (photo.name || '').toLowerCase()
            const id = (photo.id || '').toLowerCase()
            return name.includes(searchLower) || id.includes(searchLower)
        })
    }, [photos, effectiveSearch])

    const handleClearSelection = () => {
        // Clear all selections - extract IDs if objects
        selectedPhotos.forEach((item) => {
            const id = typeof item === 'string' ? item : item.id
            const source = photoSources[id]
            const photo = filteredPhotos.find(p => p.id === id)
            onPhotoSelect(id, source, photo?.url)
        })
    }

    return (
        <div className="tw:flex tw:flex-col tw:gap-4">
            {/* Search and Controls */}
            <SearchAndFilter
                showClearButton={true}
                showFormatFilter={false}
                disableClearButton={selectedPhotos.length === 0}
                searchValue={effectiveSearch}
                onClearSelection={handleClearSelection}
                searchPlaceholder="Search by file name"
                onSearchChange={effectiveOnSearchChange}
                clearButtonText="Clear selection"
            />

            <div className="tw:max-h-140 tw:overflow-auto ">
                {/* Image Grid */}
                {filteredPhotos.length === 0 ? (
                    <div className="tw:text-center tw:py-12">
                        <p className="tw:text-gray-400">
                            {effectiveSearch
                                ? `No images found matching "${effectiveSearch}"`
                                : 'No images available in the media library'}
                        </p>
                    </div>
                ) : (
                    <div className="tw:flex tw:gap-4 tw:flex-wrap">
                        {filteredPhotos.map((photo) => (
                            <Photo
                                key={photo.id}
                                photo={photo}
                                source={photoSources[photo.id]}
                                onPhotoSelect={onPhotoSelect}
                                isSelected={selectedPhotos.some(item => 
                                    typeof item === 'string' ? item === photo.id : item.id === photo.id
                                )}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Show loading state if needed */}
            {loading && (
                <div className="tw:flex tw:gap-4 tw:flex-wrap tw:mt-4">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="tw:space-y-1">
                            <div
                                className="tw:bg-gray-700 tw:animate-pulse tw:rounded-xs"
                                style={{ width: 130, height: 130 }}
                            />
                            <div className="tw:bg-gray-700 tw:animate-pulse tw:h-3 tw:rounded" style={{ width: 100 }} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default MediaLibraryGrid
